import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../api/client.ts';
import {
  callScreenGrant,
  disableNotifications,
  enableNotifications,
  isSubscribed,
  openFullScreenSettings,
  openNotificationSettings,
  pushEnvironment,
  refreshEnvironment,
  type CallScreenGrant,
} from '../lib/push.ts';
import './NewChatDialog.css';
import './NotificationSettingsDialog.css';

export function NotificationSettingsDialog({ onClose }: { onClose: () => void }) {
  const [environment, setEnvironment] = useState(pushEnvironment);
  const [showPreview, setShowPreview] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Whether this device actually has a subscription — not the same question as
  // whether the OS granted permission, and the button must follow this one.
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  /**
   * Android 14 and up: whether an incoming call may take over the screen, and
   * whether this device has that grant to give at all. Null until asked.
   */
  const [callScreen, setCallScreen] = useState<CallScreenGrant | null>(null);

  const refreshSubscribed = useCallback(async () => {
    // Asked together because in the app both answers come from the OS and only
    // the pair of them says what the button should read: iOS can hold a
    // permission with no registered device, and a device whose permission was
    // withdrawn from Settings while the app was not looking.
    setEnvironment(await refreshEnvironment());
    setSubscribed(await isSubscribed());
    setCallScreen(await callScreenGrant());
  }, []);

  useEffect(() => {
    void refreshSubscribed();
  }, [refreshSubscribed]);

  // «Разрешить» leaves the app for a system screen, and what it says there is
  // the answer this dialog is showing — so the way back has to re-ask. Without
  // it the row goes on saying «Звонки не открывают экран» to somebody who has
  // just granted exactly that, which reads as the button having done nothing.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshSubscribed();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSubscribed]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    api
      .pushSettings()
      .then((settings) => setShowPreview(settings.showPreview))
      .catch(() => {
        // Offline: the checkbox shows the default until the next open.
      })
      .finally(() => setLoading(false));
  }, []);

  // Deliberately not `permission === 'granted'`: permission outlives an
  // unsubscribe, and on iOS a subscription can lapse while permission stays
  // granted — so keying the button off permission offered «Отключить» to
  // someone who had just switched notifications on, and turned them back off.
  const active = subscribed === true;

  async function togglePreview(next: boolean) {
    // Optimistic, and rolled back on failure — this is a one-checkbox form with
    // nowhere sensible to put a spinner.
    setShowPreview(next);
    try {
      await api.savePushSettings(next);
    } catch {
      setShowPreview(!next);
      setMessage('Не удалось сохранить настройку');
    }
  }

  async function toggleEnabled() {
    setBusy(true);
    setMessage(null);
    if (active) {
      await disableNotifications().catch(() => {});
      setMessage('Уведомления отключены на этом устройстве');
    } else {
      const result = await enableNotifications();
      setMessage(result.message);
    }
    await refreshSubscribed();
    setBusy(false);
  }

  async function sendTest() {
    setBusy(true);
    try {
      const { outcome } = await api.testPush();
      // The server refuses to push to a foreground session, so an open app
      // getting nothing is correct behaviour rather than a failure.
      setMessage(
        outcome === 'skipped-active'
          ? 'Приложение открыто — уведомление не отправлено. Сверните его и попробуйте снова.'
          : outcome === 'sent'
            ? 'Уведомление отправлено'
            : 'Уведомления сейчас недоступны',
      );
    } catch {
      setMessage('Не удалось отправить уведомление');
    }
    setBusy(false);
  }

  return (
    <div className="dialog-scrim" onMouseDown={onClose}>
      <div
        className="dialog-card"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Уведомления"
      >
        <button
          type="button"
          className="icon-button dialog-close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>

        <h3 className="dialog-title">Уведомления</h3>

        {environment.needsHomeScreen ? (
          <p className="settings-note">
            На iPhone уведомления работают только в установленном приложении. Нажмите
            «Поделиться», затем «На экран „Домой“», и откройте приложение с домашнего экрана.
          </p>
        ) : !environment.supported ? (
          <p className="settings-note">
            {environment.isNative
              ? 'Эта версия приложения не умеет получать уведомления. Обновите её.'
              : 'Этот браузер не поддерживает уведомления.'}
          </p>
        ) : (
          <>
            <label className="settings-row">
              <input
                type="checkbox"
                checked={showPreview}
                disabled={loading}
                onChange={(event) => void togglePreview(event.target.checked)}
              />
              <span>
                Показывать текст сообщения
                <span className="settings-hint">
                  Иначе в уведомлении будет только имя отправителя
                </span>
              </span>
            </label>

            {/*
              The permission Android 14 stopped granting on its own, and the
              only one here that is about calls rather than about notifications.
              Refused, a ring is still a ring and is never dropped — the
              foreground service behind it is what earns the CallStyle layout
              now, not this grant. What is lost is measured rather than guessed:
              on a locked HONOR the card renders collapsed, so it reads as an
              ordinary notification, and the screen wake lock is cut to eight
              seconds by the skin, so the phone goes dark while it is still
              ringing. Granted, the same call opens full screen over the
              keyguard and stays lit for the whole ring.

              Drawn whenever the phone has this grant to give, granted or not,
              which it did not used to be. «Only when something is wrong» is the
              right instinct for a settings screen somebody is reading about
              their own phone, and the wrong one for the case this is actually
              for: handing a newly set-up phone back to the person who will be
              called on it. There is no other way to see the answer from inside
              the app, so a missing row meant «всё в порядке» and «эта сборка не
              умеет спросить» at once — and the state that matters here decides
              whether an incoming call is seen at all.
            */}
            {active && callScreen?.applicable && (
              <div className="settings-row">
                <span>
                  {callScreen.granted ? 'Звонки открывают экран' : 'Звонки не открывают экран'}
                  <span className="settings-hint">
                    {callScreen.granted
                      ? 'Разрешение «Полноэкранные уведомления» выдано: звонок открывается на весь экран и держит его до ответа'
                      : 'Разрешение называется «Полноэкранные уведомления». Без него звонок приходит карточкой в шторку, а экран вспыхивает на несколько секунд и снова гаснет. С ним — открывается на весь экран и горит до ответа'}
                  </span>
                </span>
                {!callScreen.granted && (
                  <button
                    className="settings-secondary"
                    type="button"
                    onClick={() => void openFullScreenSettings()}
                  >
                    Разрешить
                  </button>
                )}
              </div>
            )}

            <div className="settings-actions">
              <div className="settings-status">
                {subscribed === null
                  ? 'Проверяем…'
                  : active
                    ? 'Уведомления включены на этом устройстве'
                    : environment.permission === 'denied'
                      ? environment.isNative
                        ? 'Уведомления запрещены в «Настройках» iPhone'
                        : 'Уведомления запрещены в настройках браузера'
                      : 'Уведомления выключены на этом устройстве'}
              </div>
              {/*
                iOS never shows its permission alert twice, so once it has been
                refused the button below can do nothing at all and the app's own
                page in Settings is the entire remedy. Offering it in place of a
                button that would silently fail.
              */}
              {environment.isNative && !active && environment.permission === 'denied' ? (
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => void openNotificationSettings()}
                >
                  Открыть настройки
                </button>
              ) : (
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => void toggleEnabled()}
                  disabled={busy || subscribed === null}
                >
                  {active ? 'Отключить на этом устройстве' : 'Включить уведомления'}
                </button>
              )}
              {active && (
                <button
                  className="settings-secondary"
                  type="button"
                  onClick={() => void sendTest()}
                  disabled={busy}
                >
                  Проверить
                </button>
              )}
            </div>
          </>
        )}

        {message && <div className="settings-message">{message}</div>}
      </div>
    </div>
  );
}
